/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import './AdminUsers.css';
import './InstructorStudents.css';
import './InstructorStatistics.css';
import { apiFetch } from './api';
import { useActiveCourse } from './ActiveCourseContext';

const EXAM_TYPE_LABELS = {
  'odev': 'Ödev', 'Odev': 'Ödev', 'Ödev': 'Ödev',
  'final': 'Dönem Sonu Sınavı', 'Final': 'Dönem Sonu Sınavı',
  'quiz': 'Kısa Sınav', 'Quiz': 'Kısa Sınav',
  'vize': 'Ara Sınav', 'Vize': 'Ara Sınav', 'Ara Sınav': 'Ara Sınav',
  'butunleme': 'Bütünleme', 'bütünleme': 'Bütünleme', 'Butunleme': 'Bütünleme', 'Bütünleme': 'Bütünleme',
  'Ontest': 'Ön Test', 'ontest': 'Ön Test',
};
const getExamTypeLabel = (type) => EXAM_TYPE_LABELS[type] || type || '';

const ICON_BOOK = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
  </svg>
);

const avgClass = (v) => {
  if (v == null || isNaN(v)) return 'empty';
  if (v >= 70) return 'high';
  if (v >= 50) return 'mid';
  return 'low';
};

function StatCard({ label, value, accent = '#4CAF50', accentBg = '#ecfdf5', accentText = '#0f172a', icon, hint, onClick }) {
  const style = {
    '--accent': accent,
    '--accent-bg': accentBg,
    '--accent-text': accentText,
  };
  return (
    <div
      className={`st-stat-card ${onClick ? 'clickable' : ''}`}
      style={style}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {icon && <span className="st-stat-icon">{icon}</span>}
      <span className="st-stat-label">{label}</span>
      <span className="st-stat-value">{value}</span>
      {hint && (
        <span className="st-stat-hint">
          {hint}
          {onClick && (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          )}
        </span>
      )}
    </div>
  );
}

function InstructorStatistics({ instructorId, useActiveCourseSelection = true }) {
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCompletedExams, setShowCompletedExams] = useState(false);
  const [completedExams, setCompletedExams] = useState([]);
  const [loadingCompletedExams, setLoadingCompletedExams] = useState(false);
  const [selectedExam, setSelectedExam] = useState(null);
  const [examStatistics, setExamStatistics] = useState(null);
  const [loadingExamStats, setLoadingExamStats] = useState(false);
  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [courseStatistics, setCourseStatistics] = useState(null);
  const [loadingCourseStats, setLoadingCourseStats] = useState(false);
  const [rankingType, setRankingType] = useState(null);
  const [rankingStudents, setRankingStudents] = useState([]);
  const [loadingRanking, setLoadingRanking] = useState(false);
  const [courseSearch, setCourseSearch] = useState('');
  const [showCourseDropdown, setShowCourseDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const courseContext = useActiveCourse();
  const activeCourseContext = useActiveCourseSelection ? courseContext : null;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowCourseDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (instructorId !== null && instructorId !== undefined) {
      fetchStatistics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instructorId]);

  useEffect(() => {
    if (instructorId !== null && instructorId !== undefined) {
      setCoursesLoading(true);
      fetchInstructorCourses().finally(() => setCoursesLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instructorId]);

  useEffect(() => {
    if (!selectedCourseId) {
      setCourseStatistics(null);
      return;
    }
    fetchCourseStatistics(selectedCourseId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCourseId]);

  useEffect(() => {
    if (!activeCourseContext) return;
    const nextCourseId = activeCourseContext.activeCourseId;
    if (String(selectedCourseId) !== String(nextCourseId)) setSelectedCourseId(nextCourseId);
  }, [activeCourseContext, selectedCourseId]);

  useEffect(() => {
    if (instructorId === null || instructorId === undefined) return;
    const interval = setInterval(() => {
      fetchStatistics(true).catch(() => {});
    }, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instructorId]);

  const fetchInstructorCourses = async () => {
    const response = await apiFetch('/instructor/courses');
    const data = await response.json();
    if (data.success && data.courses) setCourses(data.courses);
  };

  const fetchCourseStatistics = async (courseId) => {
    try {
      setLoadingCourseStats(true);
      setCourseStatistics(null);
      setError('');
      const response = await apiFetch(`/instructor/courses/${courseId}/statistics`);
      let data;
      try {
        data = await response.json();
      } catch {
        setError(response.ok ? 'Ders istatistikleri yüklenemedi' : `Sunucu hatası (${response.status})`);
        return;
      }
      if (data.success) setCourseStatistics(data);
      else setError(data.message || 'Ders istatistikleri yüklenemedi');
    } catch (err) {
      setError('Ders istatistikleri yüklenemedi (bağlantı hatası)');
    } finally {
      setLoadingCourseStats(false);
    }
  };

  const fetchStatistics = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const response = await apiFetch(`/instructor/statistics`);
      const data = await response.json();
      if (data.success) setStatistics(data.statistics || {});
      else if (!silent) setError(data.message || 'İstatistikler yüklenemedi');
    } catch (err) {
      if (!silent) setError('Bağlantı hatası');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const fetchCompletedExams = async () => {
    try {
      setLoadingCompletedExams(true);
      setError('');
      const response = await apiFetch(`/instructor/exams/completed`);
      const data = await response.json();
      if (data.success) setCompletedExams(data.exams || []);
      else setError(data.message || 'Tamamlanmış sınavlar yüklenemedi');
    } catch {
      setError('Bağlantı hatası');
    } finally {
      setLoadingCompletedExams(false);
    }
  };

  const fetchExamStatistics = async (examId) => {
    try {
      setLoadingExamStats(true);
      const response = await apiFetch(`/instructor/exams/${examId}/statistics`);
      const data = await response.json();
      if (data.success) setExamStatistics(data.statistics);
      else setError(data.message || 'Sınav istatistikleri yüklenemedi');
    } catch {
      setError('Bağlantı hatası');
    } finally {
      setLoadingExamStats(false);
    }
  };

  const handleCompletedExamsClick = () => {
    setShowCompletedExams(true);
    setSelectedExam(null);
    setExamStatistics(null);
    fetchCompletedExams();
  };

  const handleExamSelect = (exam) => {
    setSelectedExam(exam);
    fetchExamStatistics(exam.exam_id);
  };

  const fetchExamRankings = async (examId, type) => {
    try {
      setLoadingRanking(true);
      setRankingType(type);
      setRankingStudents([]);
      const response = await apiFetch(`/instructor/exams/${examId}/statistics/rankings?type=${type}&limit=10`);
      const data = await response.json();
      if (data.success && data.students) setRankingStudents(data.students);
      else setRankingStudents([]);
    } catch {
      setRankingStudents([]);
    } finally {
      setLoadingRanking(false);
    }
  };

  const handleBackToList = () => {
    setSelectedExam(null);
    setExamStatistics(null);
    setRankingType(null);
    setRankingStudents([]);
    setError('');
  };

  const handleBackToStats = () => {
    setShowCompletedExams(false);
    setSelectedExam(null);
    setExamStatistics(null);
    setCompletedExams([]);
    setError('');
  };

  const getCourseLabel = (c) => {
    if (!c) return '';
    const code = (c.course_code || '').trim().toUpperCase();
    const name = c.course_name || `Ders ${c.course_id}`;
    return code ? `${code} — ${name}` : name;
  };

  const filteredCourses = useMemo(() => {
    const q = courseSearch.toLocaleLowerCase('tr');
    if (!q) return courses;
    return courses.filter((c) =>
      (c.course_code || '').toLocaleLowerCase('tr').includes(q) ||
      (c.course_name || '').toLocaleLowerCase('tr').includes(q) ||
      (c.program_name || '').toLocaleLowerCase('tr').includes(q) ||
      (c.faculty_name || '').toLocaleLowerCase('tr').includes(q)
    );
  }, [courses, courseSearch]);

  const selectedCourse = courses.find((c) => String(c.course_id) === String(selectedCourseId));
  const selectedLabel = selectedCourse ? getCourseLabel(selectedCourse) : 'Ders seçiniz';
  const selectCourse = (courseId) => {
    setSelectedCourseId(courseId);
    if (activeCourseContext) activeCourseContext.setActiveCourseId(courseId);
  };

  // ===== Sub-views =====

  // Exam statistics view
  if (selectedExam && examStatistics) {
    return (
      <div className="au-page">
        <div className="st-detail-header">
          <button className="st-back-btn" onClick={handleBackToList}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
            Geri
          </button>
          <div className="st-detail-title">
            <span className="st-detail-eyebrow">Sınav İstatistikleri</span>
            <h2 className="st-detail-name">{selectedExam.title}</h2>
          </div>
        </div>

        {loadingExamStats ? (
          <div className="au-card">
            <div className="au-skeleton">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="au-skeleton-row">
                  <div className="au-skeleton-block" style={{ width: '30%' }} />
                  <div className="au-skeleton-block" style={{ width: 80 }} />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="st-stat-grid">
            <StatCard
              label="Sınav Ortalaması"
              value={examStatistics.average_score.toFixed(2)}
              accent="#4CAF50"
              accentBg="#ecfdf5"
              accentText="#15803d"
              icon={
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="20" x2="18" y2="10" />
                  <line x1="12" y1="20" x2="12" y2="4" />
                  <line x1="6" y1="20" x2="6" y2="14" />
                </svg>
              }
            />
            <StatCard
              label="En Yüksek Puan"
              value={examStatistics.highest_score.toFixed(2)}
              accent="#10b981"
              accentBg="#d1fae5"
              accentText="#047857"
              icon={
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                </svg>
              }
              hint="İlk 10 öğrenciyi gör"
              onClick={() => fetchExamRankings(selectedExam.exam_id, 'top')}
            />
            <StatCard
              label="En Düşük Puan"
              value={examStatistics.lowest_score.toFixed(2)}
              accent="#ef4444"
              accentBg="#fef2f2"
              accentText="#b91c1c"
              icon={
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="22 17 13.5 8.5 8.5 13.5 2 7" />
                  <polyline points="16 17 22 17 22 11" />
                </svg>
              }
              hint="Son 10 öğrenciyi gör"
              onClick={() => fetchExamRankings(selectedExam.exam_id, 'bottom')}
            />
            <StatCard
              label="Teslim Edilen"
              value={examStatistics.submitted_count}
              accent="#3b82f6"
              accentBg="#eff6ff"
              accentText="#1d4ed8"
              icon={
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              }
            />
          </div>
        )}

        {(rankingType || loadingRanking) && (
          <div className="st-rankings-panel">
            <div className="st-rankings-header">
              <h3 className="st-rankings-title">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <line x1="3" y1="18" x2="21" y2="18" />
                </svg>
                {rankingType === 'top' ? 'En Yüksek Puan Alan İlk 10 Öğrenci' : 'En Düşük Puan Alan İlk 10 Öğrenci'}
              </h3>
              <button
                type="button"
                className="st-rankings-close"
                onClick={() => { setRankingType(null); setRankingStudents([]); }}
                aria-label="Kapat"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            {loadingRanking ? (
              <div style={{ padding: '1.5rem', textAlign: 'center', color: '#64748b' }}>Yükleniyor...</div>
            ) : rankingStudents.length === 0 ? (
              <div style={{ padding: '1.5rem', textAlign: 'center', color: '#64748b' }}>Teslim edilen sınav bulunamadı.</div>
            ) : (
              <div className="st-table-wrap">
                <table className="st-table">
                  <thead>
                    <tr>
                      <th className="center" style={{ width: 60 }}>Sıra</th>
                      <th>Öğrenci No</th>
                      <th>Ad Soyad</th>
                      <th className="right">Not</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rankingStudents.map((s) => {
                      const rankCls = s.rank === 1 ? 'gold' : s.rank === 2 ? 'silver' : s.rank === 3 ? 'bronze' : '';
                      return (
                        <tr key={s.rank}>
                          <td className="center">
                            <span className={`st-rank-cell ${rankCls}`}>{s.rank}</span>
                          </td>
                          <td>{s.student_number}</td>
                          <td style={{ fontWeight: 500 }}>{s.student_name || '—'}</td>
                          <td className="right">
                            <span className={`st-avg-pill ${s.total_points != null ? avgClass(s.total_points) : 'empty'}`}>
                              {s.total_points != null ? s.total_points.toFixed(2) : '—'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // Completed exams list
  if (showCompletedExams) {
    return (
      <div className="au-page">
        <div className="st-detail-header">
          <button className="st-back-btn" onClick={handleBackToStats}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
            Geri
          </button>
          <div className="st-detail-title">
            <span className="st-detail-eyebrow">İstatistikler</span>
            <h2 className="st-detail-name">Tamamlanmış Sınavlar</h2>
          </div>
        </div>

        {loadingCompletedExams ? (
          <div className="au-card">
            <div className="au-skeleton">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="au-skeleton-row">
                  <div className="au-skeleton-circle" />
                  <div style={{ flex: 1 }}>
                    <div className="au-skeleton-block" style={{ width: '50%', marginBottom: 6 }} />
                    <div className="au-skeleton-block" style={{ width: '30%', height: 11 }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : completedExams.length === 0 ? (
          <div className="au-card">
            <div className="au-empty">
              <div className="au-empty-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
              </div>
              <div className="au-empty-text">Tamamlanmış sınav bulunmamaktadır</div>
            </div>
          </div>
        ) : (
          <div className="st-exams-grid">
            {completedExams.map((exam) => (
              <button
                key={exam.exam_id}
                type="button"
                className="st-exam-card"
                onClick={() => handleExamSelect(exam)}
              >
                <div className="st-exam-card-top">
                  <h3 className="st-exam-card-title">{exam.title}</h3>
                  <span className="st-exam-card-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                    </svg>
                  </span>
                </div>
                <div className="st-exam-card-meta">
                  <div className="st-exam-card-meta-row">
                    <span className="st-exam-card-meta-icon">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                      </svg>
                    </span>
                    <span className="st-exam-card-meta-text">
                      <strong>Ders: </strong>{exam.course_name}
                    </span>
                  </div>
                  <div className="st-exam-card-meta-row">
                    <span className="st-exam-card-meta-icon">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                      </svg>
                    </span>
                    <span className="st-exam-card-meta-text">
                      <strong>Bitiş: </strong>{exam.end_at ? new Date(exam.end_at).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ===== Main view =====

  return (
    <div className="au-page">
      <div className="au-toolbar">
        <div className="au-toolbar-top">
          <div className="au-title-block">
            <h2 className="au-title">İstatistikler</h2>
            <p className="au-subtitle">
              {activeCourseContext
                ? 'Aktif dersin sınıf ortalamalarını ve sınav bazlı istatistiklerini görüntüleyin.'
                : 'Bir ders seçerek sınıf ortalamalarını ve sınav bazlı istatistikleri görüntüleyin.'}
            </p>
          </div>
          {selectedCourse && (
            <div className="au-count-pill">
              <span className="au-count-pill-dot" />
              {courseStatistics?.course_students || 0} öğrenci
            </div>
          )}
        </div>

        {!activeCourseContext && (
          <div className="is-course-filter" ref={dropdownRef} style={{ maxWidth: 'none' }}>
          <button
            type="button"
            className={`is-course-btn ${showCourseDropdown ? 'open' : ''}`}
            onClick={() => { setShowCourseDropdown((o) => !o); setCourseSearch(''); }}
          >
            <span className="is-course-btn-icon">{ICON_BOOK}</span>
            <span className="is-course-btn-label">{selectedLabel}</span>
            {selectedCourseId ? (
              <span
                onClick={(e) => { e.stopPropagation(); selectCourse(''); setCourseSearch(''); }}
                className="is-course-btn-chevron"
                style={{ cursor: 'pointer' }}
                title="Seçimi kaldır"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </span>
            ) : (
              <span className="is-course-btn-chevron">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </span>
            )}
          </button>
          {showCourseDropdown && (
            <div className="is-course-panel">
              <div className="is-course-search-wrap">
                <span className="is-course-search-icon">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                </span>
                <input
                  autoFocus
                  type="text"
                  className="is-course-search-input"
                  placeholder={coursesLoading ? 'Dersler yükleniyor...' : 'Ders kodu veya adı ara...'}
                  disabled={coursesLoading}
                  value={courseSearch}
                  onChange={(e) => setCourseSearch(e.target.value)}
                />
              </div>
              <ul className="is-course-list">
                {filteredCourses.length === 0 ? (
                  <li className="is-course-empty">Sonuç bulunamadı</li>
                ) : (
                  filteredCourses.map((c) => {
                    const isSel = selectedCourseId === c.course_id;
                    return (
                      <li
                        key={c.course_id}
                        className={`is-course-item ${isSel ? 'selected' : ''}`}
                        onClick={() => { selectCourse(c.course_id); setShowCourseDropdown(false); setCourseSearch(''); }}
                      >
                        <span className="is-course-item-label">{getCourseLabel(c)}</span>
                        {(c.faculty_name || c.program_name) && (
                          <span className="is-course-item-badges">
                            {c.faculty_name && (
                              <span className="is-course-item-faculty">{c.faculty_name}</span>
                            )}
                            {c.program_name && (
                              <span className="is-course-item-program">{c.program_name}</span>
                            )}
                          </span>
                        )}
                        {isSel && (
                          <span className="is-course-item-check">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          </span>
                        )}
                      </li>
                    );
                  })
                )}
              </ul>
            </div>
          )}
          </div>
        )}
      </div>

      {error && (
        <div className="au-flash error">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          {error}
        </div>
      )}

      {!selectedCourseId ? (
        <div className="au-card">
          <div className="au-empty">
            <div className="au-empty-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="20" x2="18" y2="10" />
                <line x1="12" y1="20" x2="12" y2="4" />
                <line x1="6" y1="20" x2="6" y2="14" />
              </svg>
            </div>
            <div className="au-empty-text">İstatistikleri görüntülemek için bir ders seçin</div>
            <div className="au-empty-sub">
              {activeCourseContext
                ? 'Sayfanın üstündeki Aktif Ders alanından ders seçebilirsiniz.'
                : 'Yukarıdaki seçim kutusundan ders kodu veya adıyla arama yapabilirsiniz.'}
            </div>
          </div>
        </div>
      ) : loadingCourseStats ? (
        <>
          <div className="st-stat-grid">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="st-stat-card" style={{ '--accent': '#e2e8f0' }}>
                <div className="au-skeleton-block" style={{ width: 38, height: 38, borderRadius: 10 }} />
                <div className="au-skeleton-block" style={{ width: '60%', height: 10 }} />
                <div className="au-skeleton-block" style={{ width: '40%', height: 28 }} />
              </div>
            ))}
          </div>
        </>
      ) : courseStatistics ? (
        <>
          <div className="st-stat-grid">
            <StatCard
              label="Tamamlanmış Sınav"
              value={courseStatistics.course_completed_exams || 0}
              accent="#4CAF50"
              accentBg="#ecfdf5"
              accentText="#15803d"
              icon={
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              }
              hint="Sınavları görüntüle"
              onClick={handleCompletedExamsClick}
            />
            <StatCard
              label="Toplam Öğrenci"
              value={courseStatistics.course_students || 0}
              accent="#3b82f6"
              accentBg="#eff6ff"
              accentText="#1d4ed8"
              icon={
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              }
            />
            <StatCard
              label="Bekleyen Sınavlar"
              value={courseStatistics.course_pending_exams || 0}
              accent="#f59e0b"
              accentBg="#fef3c7"
              accentText="#b45309"
              icon={
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
              }
            />
            <StatCard
              label="Sınıf Ortalaması"
              value={(typeof courseStatistics.department_average === 'number'
                ? courseStatistics.department_average
                : (courseStatistics.class_average ?? 0)
              ).toFixed(2)}
              accent="#10b981"
              accentBg="#d1fae5"
              accentText="#047857"
              icon={
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              }
            />
          </div>

          {courseStatistics.exam_weights && courseStatistics.exam_weights.length > 0 && (
            <div className="st-section">
              <div className="st-section-header">
                <h3 className="st-section-title">
                  <span className="st-section-title-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="12" y1="20" x2="12" y2="10" />
                      <line x1="18" y1="20" x2="18" y2="4" />
                      <line x1="6" y1="20" x2="6" y2="16" />
                    </svg>
                  </span>
                  Sınav ağırlıkları ve tür ortalamaları
                </h3>
              </div>
              <div className="st-table-wrap">
                <table className="st-table">
                  <thead>
                    <tr>
                      <th>Sınav Türü</th>
                      <th className="center" style={{ width: 140 }}>Ağırlık</th>
                      <th className="right" style={{ width: 160 }}>Not Ortalaması</th>
                    </tr>
                  </thead>
                  <tbody>
                    {courseStatistics.exam_type_averages && courseStatistics.exam_type_averages.length > 0
                      ? courseStatistics.exam_type_averages.map((eta) => {
                          const w = courseStatistics.exam_weights.find((ew) => ew.id === eta.exam_weight_id)?.weight;
                          return (
                            <tr key={eta.exam_weight_id}>
                              <td><span className="st-exam-type-tag">{getExamTypeLabel(eta.exam_type)}</span></td>
                              <td className="center">
                                <span className="st-weight-badge">%{w ?? 0}</span>
                              </td>
                              <td className="right">
                                <span className={`st-avg-pill ${avgClass(eta.average)}`}>
                                  {eta.average != null ? eta.average.toFixed(2) : '—'}
                                </span>
                              </td>
                            </tr>
                          );
                        })
                      : courseStatistics.exam_weights.map((ew) => (
                          <tr key={ew.id}>
                            <td><span className="st-exam-type-tag">{getExamTypeLabel(ew.exam_type)}</span></td>
                            <td className="center"><span className="st-weight-badge">%{ew.weight}</span></td>
                            <td className="right"><span className="st-avg-pill empty">—</span></td>
                          </tr>
                        ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="st-section">
            <div className="st-section-header">
              <h3 className="st-section-title">
                <span className="st-section-title-icon">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                  </svg>
                </span>
                Sınavlar
              </h3>
              <span className="st-section-sub">{(courseStatistics.exams || []).length} sınav</span>
            </div>
            <div className="st-table-wrap">
              <table className="st-table">
                <thead>
                  <tr>
                    <th>Sınav Adı</th>
                    <th>Sınav Türü</th>
                    <th className="center">Giren Öğrenci</th>
                    <th className="right">Ortalama</th>
                  </tr>
                </thead>
                <tbody>
                  {courseStatistics.exams && courseStatistics.exams.length > 0 ? (
                    courseStatistics.exams.map((ex) => (
                      <tr key={ex.id}>
                        <td className="st-exam-title-cell">{ex.title}</td>
                        <td><span className="st-exam-type-tag">{getExamTypeLabel(ex.exam_type) || '—'}</span></td>
                        <td className="center">{ex.submitted_count}</td>
                        <td className="right">
                          <span className={`st-avg-pill ${ex.average_score != null ? avgClass(ex.average_score) : 'empty'}`}>
                            {ex.average_score != null ? ex.average_score.toFixed(2) : '—'}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4}>
                        <div className="au-empty" style={{ padding: '2rem 1rem' }}>
                          <div className="au-empty-icon">
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                              <polyline points="14 2 14 8 20 8" />
                            </svg>
                          </div>
                          <div className="au-empty-text">Bu derste henüz sınav yok</div>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

export default InstructorStatistics;
